import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Dialog,
  Form,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  RadioGroup,
  SecondaryBtn,
  Select,
  WaitingAlert,
  WarningAlert,
  CloseDialogBtn,
  SuccessAlert,
  TertiaryBtn,
  SvgExternal,
} from '@tmapy/style-guide';

import type { DocRequest, PatchedDocRequest } from '../../openapi/types';
import { DtmDataTypeEnum } from '../../openapi/types/DtmDataTypeEnum';
import { DocCheckStateEnum } from '../../openapi/types/DocCheckStateEnum';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';
import { UtilitiesTypeEnum } from '../../openapi/types/UtilitiesTypeEnum';
import { useDocsRetrieveResource } from '../../openapi/operations/docsRetrieve';
import { docsCreate } from '../../openapi/operations/docsCreate';
import { docsCreateCopyCreate } from '../../openapi/operations/docsCreateCopyCreate';
import { docsDestroy } from '../../openapi/operations/docsDestroy';
import { docsCheckCreate } from '../../openapi/operations/docsCheckCreate';
import { docsImportDocCreate } from '../../openapi/operations/docsImportDocCreate';
import { docsListOperation } from '../../openapi/operations/docsList';
import { docsPartialUpdate } from '../../openapi/operations/docsPartialUpdate';
import { docsEditCheckCreate } from '../../openapi/operations/docsEditCheckCreate';
import { dmvsGadDocsCheckDmvsPendingPozadavkyCreate } from '../../openapi/operations/dmvsGadDocsCheckDmvsPendingPozadavkyCreate';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LogList } from '../../logs/components/LogList';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAppContext,
  useAuth,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import {
  checkSourceEnum,
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
  DOCUMENTATION_TYPES,
  REJECTION_REASON_STATES,
} from '../constants';
import { loadDoc, updateDoc } from '../actions/docDetail';
import { getFormValues } from '../utils/getformvalues';

import {
  docFormMessages,
  docTitleMessages,
  documentationTypesMessages,
  docUtilitiesTypeMessages,
  checkTypesMessages,
  rejectionMessages,
  rejectionReasonMessages,
} from './docsMessages';
import { docEditButtonsMsg, docEditMessages } from './docEditMessages';

import { checkSourceMessages } from './checkMessages';
import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { CheckRecordList } from './CheckRecordList';
import { ResponseErrorMessage } from './ResponseErrorMessage';

import './Doc.scss';

export type DocCheckEditContainerProps = {
  docId?: number;
};

export const DocCheck: React.FC<DocCheckEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const { appType } = useAppContext();

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkQueryState, setCheckQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkQueryMessage, setCheckQueryMessage] = useState<
    { code: string; message: string }[] | null
  >(null);

  const [importQueryState, setImportQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkDmvsPendingPozadavkyQueryState, setCheckDmvsPendingPozadavkyQueryState] =
    useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [copyQueryState, setCopyQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [isLogDialogShown, showLogDialog] = useState(false);
  const [checkLogId, setCheckLogId] = useState<number | undefined>(undefined);
  const [importError, setImportError] = useState<any>(null);

  const attachments = useSelector((state) => state.doc.data.attachments);
  const removeQueryState = useSelector((state) => state.doc.removeQueryState);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);
  const data = useMemo(() => {
    const newData = resource.data
      ? { ...resource.data, attachments }
      : {
          ...defaultValues,
          state: DOC_STATE.DRAFT,
          prejimka_type: PrejimkaTypesEnum.CHECK,
          flow: FlowEnum.CHECK,
        };

    return newData;
  }, [resource.data, attachments]);

  const isChecked = data.state === DOC_STATE.DRAFT_CHECKED;

  const auth = useAuth();
  const acl = useMemo(
    () => ({
      canUpdate: auth.hasPermission('Docs', 'partial_update', data),
      docsUpdate: auth.hasPermission('Docs', 'update', data),
      docsDestroy: auth.hasPermission('Docs', 'destroy', data),
      docsActionEditCheck: auth.hasPermission('Docs', 'action.edit_check', data),
      docsActionChecksVisibility: auth.hasPermission('Docs', 'checks_visibility', data),

      // restart dmvs pending
      docsActionCheckDmvsPendingPozadavky: auth.hasPermission(
        'Docs',
        'check_dmvs_pending_pozadavky',
        data,
      ),
    }),
    [auth, data],
  );
  const routeList = useHistoryAction('checkList', { appType });
  const routeDetail = useHistoryAction('docCheck', { appType, id: docId! });
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const routeToCheckDetail = useHistoryAction('docCheck', { appType, id: resource?.data?.id! });

  const showChecks = acl.docsActionChecksVisibility;

  const isAnyCheckStateActive = useMemo(() => {
    return !!data?.checks?.find((check) => {
      return (
        check.state === DocCheckStateEnum.CHECKING ||
        check.state === DocCheckStateEnum.WAITING_FOR_CHECKING
      );
    });
  }, [data?.checks]);

  useEffect(() => {
    if (!docId) {
      setSaveQueryState(QUERY_STATE.EMPTY);
    }
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [docId, resource.data, dispatch, setSaveQueryState]);

  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
  });

  const handleSaveDocWithValidation = async () => {
    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      return await handleSaveDocWithoutValidation();
    }
    return null;
  };

  const handleSaveDocWithoutValidation = async () => {
    const formValues = getFormValues(formItems) as any;
    const values = {
      ...formValues,
      // multiselect
      utilities_type: formValues.utilities_type,
      // BEG validation param - TODO remove ?
      prejimka_type: PrejimkaTypesEnum.CHECK,
    };

    return await handleSave(values);
  };

  const handleSave = useCallback(
    async (values: DocRequest | PatchedDocRequest) => {
      setSaveQueryState(QUERY_STATE.WAITING);
      let response;

      try {
        if (docId) {
          response = await docsPartialUpdate({
            query: { id: docId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
            dispatch(updateDoc(response.data));
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await docsCreate({
            request: values as DocRequest,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ appType, id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } finally {
        const docsList = docsListOperation();
        dispatch(actionResourceInvalidate(docsList.path));
      }
      if (response.ok) {
        const attachments = response.data.attachments ?? [];
        return {
          download_attachments_link: '',
          ...response.data,
          attachments,
        };
      }
      return null;
    },
    [docId, routeDetail, appType, dispatch],
  );

  const handleDelete = useCallback(async () => {
    try {
      setDeleteQueryState(QUERY_STATE.WAITING);
      const response = await docsDestroy({ query: { id: docId! } });
      if (response.ok) {
        setDeleteQueryState(QUERY_STATE.SUCCESS);

        routeList();
      } else {
        setDeleteQueryState(QUERY_STATE.FAIL);
      }
      return response;
    } catch (e) {
      setDeleteQueryState(QUERY_STATE.FAIL);
    } finally {
      const docsList = docsListOperation();
      dispatch(actionResourceInvalidate(docsList.path));
    }
    return null;
  }, [routeList, setDeleteQueryState, dispatch, docId]);

  const handleEditCheckBtnClick = () => {
    doAction(docsEditCheckCreate, setCheckQueryState);
  };
  const handleCheckDmvsPendingPozadavkyBtnClick = () => {
    doAction(dmvsGadDocsCheckDmvsPendingPozadavkyCreate, setCheckDmvsPendingPozadavkyQueryState);
  };

  const handleCheckClick = useCallback((id: number) => {
    setCheckLogId(id);
    showLogDialog(true);
  }, []);

  const handleCheckBtnClick = async () => {
    const data = await handleSaveDocWithValidation();

    if (data && data.id) {
      setCheckQueryState(QUERY_STATE.WAITING);
      setCheckQueryMessage(null);
      const checkResponse = await docsCheckCreate({
        query: { id: data.id },
      });
      if (checkResponse.ok) {
        setCheckQueryState(QUERY_STATE.SUCCESS);
        routeToCheckDetail.replace({ id: data.id });
      } else {
        setCheckQueryState(QUERY_STATE.FAIL);
        if (checkResponse.error.data) {
          setCheckQueryMessage(checkResponse.error.data);
        }
      }
      handleRefetch();
    }
  };

  const doImport = async () => {
    const importResponse = await docsImportDocCreate({ query: { id: data.id }, request: {} });

    if (importResponse.error) {
      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);

      // TODO: Predelat POUZE na resource.refresh()
      // await handleRefetch()
      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);

      setImportQueryState(QUERY_STATE.FAIL);
      setImportError(importResponse.error);
    } else {
      // refresh
      setImportQueryState(QUERY_STATE.SUCCESS);

      // TODO: Predelat POUZE na resource.refresh()
      // await handleRefetch()
      await Promise.all([handleRefetch(), dispatch(loadDoc(data.id))]);
    }
  };

  // HOOK FOR INPUTS

  const hasPlanimetryRef = useRef(!!data.has_planimetry);
  const hasUtilitiesRef = useRef(!!data.has_utilities);

  useEffect(() => {
    hasPlanimetryRef.current = !!data.has_planimetry;
    hasUtilitiesRef.current = !!data.has_utilities;
  }, [data.has_planimetry, data.has_utilities]);

  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
  }, [setSaveQueryState]);

  const utilitiesTypeItems = useSelectMessages(docUtilitiesTypeMessages, UtilitiesTypeEnum);
  const documentationTypeItems = useSelectMessages(documentationTypesMessages, DOCUMENTATION_TYPES);
  const checkTypeItems = useSelectMessages(checkTypesMessages, DtmDataTypeEnum);

  // pokud vyberu 'GP', automaticky se predvybere has_planimetry -> ref true
  const documentationTypeChangeCallback = useCallback(
    // TODO: Refactoring tech dept: any
    (e: any) => {
      if (e.target.value === DOCUMENTATION_TYPES.GP) {
        hasPlanimetryRef.current = true;
      }
      formItemChangeCallback();
    },
    [formItemChangeCallback],
  );
  const documentation_type = useInput({
    name: 'documentation_type',
    initialValue: data.documentation_type ? `${data.documentation_type}` : null,
    items: documentationTypeItems,
    label: formatMessage(docFormMessages.documentation_type),
    onChange: documentationTypeChangeCallback,
    ...formItemServerErrors.documentation_type,
  });

  const data_type = useInput({
    name: 'data_type',
    isDisabled: !acl.canUpdate,
    initialValue: data.data_type ?? '',
    items: checkTypeItems,
    label: formatMessage(docFormMessages.check_type),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.data_type,
  });

  const utilities_type = useInput({
    name: 'utilities_type',
    initialValue: data.utilities_type || UtilitiesTypeEnum.ALL_UTILITIES,
    items: utilitiesTypeItems,
    label: formatMessage(docFormMessages.utilities_type),
    onChange: formItemChangeCallback,
    isRequired: data_type.value === DtmDataTypeEnum.TI,
  });

  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: !acl.canUpdate,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  const dmvs_id_podani = useInput<InputProps>({
    name: 'dmvs_id_podani',
    isDisabled: true,
    initialValue: data.dmvs_id_podani,
    label: formatMessage(docFormMessages.dmvs_id_podani),
  });

  const geodesist_official_number = useInput<InputProps>({
    name: 'geodesist_official_number',
    isDisabled: true,
    initialValue: data.geodesist?.official_number ? `${data.geodesist.official_number}` : null,
    label: formatMessage(docFormMessages.azi_cislo_opravneni),
  });

  // CHECK SOURCE
  const checkSourceItems = useSelectMessages(checkSourceMessages, checkSourceEnum);
  const check_source = useInput({
    name: 'check_source',
    isDisabled: true,
    initialValue: data.dmvs_source ? checkSourceEnum.ISDMVS : checkSourceEnum.ISDTMK,
    items: checkSourceItems,
    label: formatMessage(docFormMessages.check_source),
  });

  const state = { value: data.state };
  const formItems = {
    state,
    check_source,
    data_type,
    name,
    utilities_type,
  } as const;

  const uploadHelpText = formatMessage.fallback(['document.check.uploadHelpText']);

  const isUtilitiesTypeDisabled =
    data_type.value !== DtmDataTypeEnum.TI || documentation_type.value === DOCUMENTATION_TYPES.GP;

  const handleCancelLogDialogBtnClick = useCallback(() => {
    setCheckLogId(undefined);
    showLogDialog(false);
  }, []);

  // PODANI
  const routePodaniKADDetail = useHistoryAction('podaniKADDetail');
  const routePodaniDTIDetail = useHistoryAction('podaniDTIDetail');
  const route =
    data_type.value === DtmDataTypeEnum.ZPS ? routePodaniKADDetail : routePodaniDTIDetail;
  const handleCreateCopyDoc = useCallback(async () => {
    try {
      setCopyQueryState(QUERY_STATE.WAITING);
      const response = await docsCreateCopyCreate({
        query: { id: docId },
      });
      if (response.ok) {
        setCopyQueryState(QUERY_STATE.SUCCESS);
        response.data.id && route.replace({ id: response.data.id });
      } else {
        setCopyQueryState(QUERY_STATE.FAIL);
      }
    } catch (e) {
      setCopyQueryState(QUERY_STATE.FAIL);
    }
  }, [route]);

  const handleCuzkIdPodaniClick = useCallback(() => {
    const url = `https://dmvs.cuzk.gov.cz/portal/gad/stav-zpracovani?id=${dmvs_id_podani.value}`;
    window.open(url, '_blank');
  }, [dmvs_id_podani.value]);

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, 'check', {
                    id: data.id || 'new',
                  })}
                </PageTitle>
              </header>
              <BtnBar>
                {acl.docsUpdate && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSaveDocWithValidation}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {isChecked && (
                  <CtaBtn
                    queryState={toBtnQueryState(copyQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCreateCopyDoc}
                  >
                    <FormattedMessage {...docEditMessages.docEditBtn} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionEditCheck && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkQueryState)}
                    isDisabled={isActionRunning || isAnyCheckStateActive}
                    onClick={handleEditCheckBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnEditCheck} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsDestroy && !data.dmvs_source && (
                  <SecondaryBtn
                    isDisabled={isActionRunning || removeQueryState === QUERY_STATE.WAITING}
                    isDanger
                    onClick={handleDelete}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRemove} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCheckDmvsPendingPozadavky && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkDmvsPendingPozadavkyQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCheckDmvsPendingPozadavkyBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCheckDmvsPendingPozadavky} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.WAITING ||
              copyQueryState === QUERY_STATE.WAITING ||
              deleteQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(saveQueryState === QUERY_STATE.FAIL || deleteQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.SUCCESS && (
              <SuccessAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateSuccess} />
              </SuccessAlert>
            )}
            {checkQueryState === QUERY_STATE.WAITING && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.checkQueryStateWaiting} />
              </WaitingAlert>
            )}
            {checkQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.checkQueryStateFail} />
                {checkQueryMessage?.map((msg, index) => (
                  <React.Fragment key={index}>
                    <br />
                    <ResponseErrorMessage error={msg} />
                  </React.Fragment>
                ))}
              </DangerAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {copyQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.copyQueryStateFail} />
              </DangerAlert>
            )}
            {([DOC_STATE.REJECTED] as DOC_STATE[]).includes(data.state) &&
              (resource.queryState === QUERY_STATE.SUCCESS || !docId) &&
              data.rejection_reason &&
              REJECTION_REASON_STATES.includes(data.state) && (
                <WarningAlert>
                  <b className='sg-u-type-bold'>
                    <FormattedMessage {...rejectionMessages.reasonTitle} />
                    {' - '}
                    <FormattedMessage
                      {...rejectionReasonMessages[data.rejection_reason ?? 'null']}
                    />
                  </b>
                  {data.rejection_note && (
                    <p className='dmx-doc--warningAlertText sg-a-mt-1/2'>{data.rejection_note}</p>
                  )}
                </WarningAlert>
              )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSaveDocWithValidation}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      <FormRow>
                        <FormItem {...name}>
                          <Input {...name} />
                        </FormItem>
                      </FormRow>

                      {!!docId && (
                        <FormRow>
                          <FormItem {...dmvs_id_podani}>
                            <Input {...dmvs_id_podani}>
                              {dmvs_id_podani.value && (
                                <TertiaryBtn
                                  icon={{ element: <SvgExternal /> }}
                                  tooltip={formatMessage(docFormMessages.dmvs_id_podani_link)}
                                  onClick={handleCuzkIdPodaniClick}
                                  size='sml'
                                />
                              )}
                            </Input>
                          </FormItem>
                        </FormRow>
                      )}

                      {data.prejimka_type === PrejimkaTypesEnum.DMVS_GAD_CHECK && (
                        <FormRow>
                          <FormItem {...geodesist_official_number}>
                            <Input {...geodesist_official_number} />
                          </FormItem>
                        </FormRow>
                      )}

                      <FormRow>
                        <FormItem {...check_source}>
                          <Select hasEmptyItem {...check_source} />
                        </FormItem>
                      </FormRow>

                      <FormRow>
                        <FormItem {...data_type}>
                          <RadioGroup {...data_type} direction='row' />
                        </FormItem>
                      </FormRow>

                      <FormRow>
                        <FormItem {...utilities_type}>
                          <Select {...utilities_type} isDisabled={isUtilitiesTypeDisabled} />
                        </FormItem>
                      </FormRow>
                    </div>
                  </section>
                </div>
              </Form>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                  {uploadHelpText && (
                    <div className='sg-a-mw-fc'>
                      <InfoAlert>{uploadHelpText}</InfoAlert>
                    </div>
                  )}
                  <DocAttachments
                    doc={data}
                    prejimkaType={data.prejimka_type}
                    createDoc={handleSaveDocWithoutValidation}
                    attachments={attachments}
                    importError={importError}
                    importQueryState={importQueryState}
                    onCheckBtnClick={!isChecked ? handleCheckBtnClick : undefined}
                    isCheckBtnDisabled={isAnyCheckStateActive}
                  />
                </div>
              </section>
            </div>

            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {resource?.data?.checks && resource?.data?.checks.length > 0 && showChecks && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2'>
                    <FormattedMessage {...docFormMessages.checks} />
                  </h3>
                  <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
                    <CheckRecordList
                      docId={docId}
                      items={resource?.data?.checks}
                      onClick={handleCheckClick}
                      onRefetch={handleRefetch}
                    />
                  </div>
                </section>
              )}
            </div>
          </div>

          {isLogDialogShown && (
            <Dialog
              onEscKeyDown={handleCancelLogDialogBtnClick}
              onOverlayClick={handleCancelLogDialogBtnClick}
            >
              <CloseDialogBtn onClick={handleCancelLogDialogBtnClick} />
              <div className='sg-a-p-4 sg-u-vs-2'>
                <PageTitle>
                  <FormattedMessage {...docFormMessages.logs} />
                </PageTitle>
                <LogList objectId={checkLogId!} resourceType={'check'} />
              </div>
            </Dialog>
          )}
        </article>
      )}
    </MainContent>
  );
};
